/**
 * RCP portal — route tree + providers.
 *
 * Path-module schema (Phase A, Bill 2026-07-07 — this app REPLACES the
 * standalone "Coming Soon" placeholder on the pcb-portal SWA):
 *
 *   /          — the pre-launch Coming Soon placeholder (public root; also the
 *                catch-all for unknown paths). No providers, no MSAL boot.
 *   /dsp/sign  — full-screen, token-authorized SIGNER KIOSK (Disclosures &
 *                Signatures module). Isolated: NO portal chrome, NO B2C auth
 *                (the capability token `?t=` is the authority).
 *   /signer    — alias for /dsp/sign (already-minted QR links keep working).
 *   /rcp       — the (not-yet-launched) customer portal landing; the rest of
 *                the portal routes keep their original paths under
 *                PortalLayout: AuthProvider → ToastProvider → PortalShell →
 *                FocusManager → <Outlet/> → SessionTimeoutModal.
 */

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/a11y/AccessibleToast';
import { PortalShell } from './components/layout/PortalShell';
import { FocusManager } from './components/a11y/FocusManager';
import { SessionTimeoutModal } from './components/a11y/SessionTimeoutModal';
import { RequireAuth } from './routes/RequireAuth';

import { SignerKiosk } from './screens/signer/SignerKiosk';
import { ComingSoon } from './screens/placeholder/ComingSoon';
import { Landing } from './screens/landing/Landing';
import { EmailOtpVerify } from './screens/auth/EmailOtpVerify';
import { Dashboard } from './screens/dashboard/Dashboard';
import { Inbox } from './screens/inbox/Inbox';
import { Preferences } from './screens/preferences/Preferences';
import { StatusPeek } from './screens/status/StatusPeek';
import { AppointmentView } from './screens/appointment/AppointmentView';
import { CipBlockHandoff } from './screens/appointment/CipBlockHandoff';

import { ApplyWizard } from './screens/apply/ApplyWizard';
import { Step1GetStarted } from './screens/apply/Step1GetStarted';
import { Step2Applicant } from './screens/apply/Step2Applicant';
import { Step3CipId } from './screens/apply/Step3CipId';
import { Step4Disclosures } from './screens/apply/Step4Disclosures';
import { Step5Funding } from './screens/apply/Step5Funding';
import { Step6Success } from './screens/apply/Step6Success';

function PortalLayout(): JSX.Element {
  return (
    <AuthProvider>
      <ToastProvider>
        <PortalShell>
          <FocusManager />
          <Outlet />
          <SessionTimeoutModal />
        </PortalShell>
      </ToastProvider>
    </AuthProvider>
  );
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public root — the Coming Soon placeholder (customers must see no
            change from the standalone placeholder app this replaced). Outside
            PortalLayout so hitting portal.mypcb.com never boots MSAL. */}
        <Route path="/" element={<ComingSoon />} />

        {/* Signer kiosk — isolated full-screen surface, token-authorized.
            /dsp/sign is canonical; /signer is a legacy alias (minted QR links). */}
        <Route path="/dsp/sign" element={<SignerKiosk />} />
        <Route path="/signer" element={<SignerKiosk />} />

        {/* Customer portal — providers + shell. */}
        <Route element={<PortalLayout />}>
          <Route path="/rcp" element={<Landing />} />
          <Route path="/auth/signin" element={<EmailOtpVerify />} />
          <Route path="/auth/callback" element={<EmailOtpVerify />} />

          {/* Apply Now — 6 steps (unauthenticated for step 1; steps 2-6
              are draft-owned by the session — the Function resolves
              identity from the B2C token once signed in). */}
          <Route path="/apply" element={<ApplyWizard />}>
            <Route index element={<Navigate to="start" replace />} />
            <Route path="start" element={<Step1GetStarted />} />
            <Route path="applicant" element={<Step2Applicant />} />
            <Route path="cip-id" element={<Step3CipId />} />
            <Route path="disclosures" element={<Step4Disclosures />} />
            <Route path="funding" element={<Step5Funding />} />
            <Route path="success" element={<Step6Success />} />
          </Route>

          {/* Authenticated pages */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/inbox"
            element={
              <RequireAuth>
                <Inbox />
              </RequireAuth>
            }
          />
          <Route
            path="/preferences"
            element={
              <RequireAuth>
                <Preferences />
              </RequireAuth>
            }
          />
          <Route
            path="/status/:cartId"
            element={
              <RequireAuth>
                <StatusPeek />
              </RequireAuth>
            }
          />
          <Route
            path="/appointment/view"
            element={
              <RequireAuth>
                <AppointmentView />
              </RequireAuth>
            }
          />
          <Route path="/cip-handoff" element={<CipBlockHandoff />} />
        </Route>

        {/* Unknown paths render the placeholder too (no MSAL boot). */}
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    </BrowserRouter>
  );
}
